import React, { useEffect } from 'react';
import './GuideModal.css';

/**
 * GuideModal — renders a self-contained HTML guide (served from /public)
 * inside an isolated <iframe> within a modal overlay.
 *
 * The guides ship their own global CSS resets, full-screen overlays and
 * <script> interactivity, so they must run in an iframe (not inlined) to
 * stay fully functional and isolated from the app's styles.
 *
 * Props:
 *   open    — whether the modal is visible
 *   onClose — called when the user closes it (backdrop, ✕, or Escape)
 *   src     — public URL of the guide HTML (e.g. /guides/MyMaterials_Workflow.html)
 *   title   — accessible title for the iframe
 */
const GuideModal = ({ open, onClose, src, title }) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="guide-modal-overlay" onClick={onClose}>
      <div className="guide-modal" onClick={(e) => e.stopPropagation()}>
        <div className="guide-modal__bar">
          <span className="guide-modal__title">{title}</span>
          <button
            type="button"
            className="guide-modal__close"
            onClick={onClose}
            aria-label="Close guide"
          >
            ✕
          </button>
        </div>
        <iframe className="guide-modal__frame" src={src} title={title} />
      </div>
    </div>
  );
};

export default GuideModal;
